import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { HeartPulse, Smile, Heart, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import MicButton from "@/components/MicButton";

const SCALE = [1, 2, 3, 4, 5];
const MOOD_LABELS = { 1: "Struggling", 2: "Low", 3: "Okay", 4: "Good", 5: "Wonderful" };
const CONN_LABELS = { 1: "Distant", 2: "A bit off", 3: "Steady", 4: "Close", 5: "Deeply connected" };

export default function CheckIn() {
  const [checkins, setCheckins] = useState(null);
  const [mood, setMood] = useState(0);
  const [connection, setConnection] = useState(0);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/checkins").then((r) => setCheckins(r.data)).catch(() => setCheckins([]));
  useEffect(() => {
    load();
  }, []);

  const submit = async () => {
    if (!mood || !connection) {
      toast.error("Pick a mood and a closeness rating first.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/checkins", { mood, connection, note });
      setCheckins((prev) => [data, ...(prev || [])]);
      setMood(0);
      setConnection(0);
      setNote("");
      toast.success("Check-in saved. Small moments add up.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/checkins/${id}`);
      setCheckins((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const recent = (checkins || []).slice(0, 14).slice().reverse();
  const avgConn =
    checkins && checkins.length
      ? (checkins.reduce((s, c) => s + (c.connection || 0), 0) / checkins.length).toFixed(1)
      : null;

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="flex items-center gap-3 mb-2">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#F5F2EA]">
            <HeartPulse className="h-5 w-5 text-[#4A5D4E]" strokeWidth={1.5} />
          </span>
          <h1 className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2D2A26]">Relationship check-in</h1>
        </div>
        <p className="text-[#6E6860] mb-8">
          A gentle weekly (or daily) pulse. Take it together or on your own — there are no wrong answers.
        </p>

        {/* Form */}
        <div className="rounded-2xl bg-white border border-[#E8E3D9] p-6 sm:p-8 shadow-sm" data-testid="checkin-form">
          <ScaleRow
            icon={Smile}
            label="How are you feeling today?"
            labels={MOOD_LABELS}
            value={mood}
            onChange={setMood}
            testPrefix="mood"
          />
          <div className="mt-8">
            <ScaleRow
              icon={Heart}
              label="How connected do you feel to each other right now?"
              labels={CONN_LABELS}
              value={connection}
              onChange={setConnection}
              testPrefix="connection"
            />
          </div>

          <div className="mt-8">
            <label className="block text-base font-medium text-[#2D2A26] mb-2">Anything you want to note? (optional)</label>
            <div className="relative">
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="A win, a worry, something you appreciated about each other…"
                data-testid="checkin-note-input"
                className="w-full rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 pr-12 text-[#2D2A26] placeholder:text-[#A39E94] focus:ring-2 focus:ring-[#4A5D4E] focus:outline-none transition resize-none"
              />
              <MicButton value={note} onChange={setNote} className="absolute bottom-2.5 right-2.5" testId="checkin-note-mic" />
            </div>
          </div>

          <button
            onClick={submit}
            disabled={saving}
            data-testid="checkin-submit-button"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#4A5D4E] text-[#FDFBF7] font-medium hover:bg-[#3B4A3E] transition-all hover:-translate-y-0.5 disabled:opacity-60"
          >
            <Check className="h-4 w-4" /> {saving ? "Saving…" : "Save check-in"}
          </button>
        </div>

        {/* Trend */}
        {recent.length > 1 && (
          <div className="mt-10 rounded-2xl bg-[#F5F2EA] border border-[#E8E3D9] p-6" data-testid="checkin-trend">
            <div className="flex items-baseline justify-between mb-4">
              <p className="text-sm uppercase tracking-[0.2em] text-[#6E6860]">Closeness over time</p>
              {avgConn && <p className="text-sm text-[#4A5D4E] font-medium">Avg {avgConn} / 5</p>}
            </div>
            <div className="flex items-end gap-2 h-32">
              {recent.map((c) => (
                <div key={c.id} className="flex-1 flex flex-col items-center gap-1.5">
                  <div
                    className="w-full rounded-t-md bg-[#4A5D4E]/80"
                    style={{ height: `${(c.connection / 5) * 100}%` }}
                    title={`${new Date(c.created_at).toLocaleDateString()} — closeness ${c.connection}/5`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* History */}
        <section className="mt-10">
          <h2 className="font-serif text-xl text-[#2D2A26] mb-4">Past check-ins</h2>
          {checkins === null ? (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 rounded-2xl bg-white border border-[#E8E3D9] animate-breathe" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          ) : checkins.length === 0 ? (
            <div className="rounded-2xl bg-[#F5F2EA] border border-dashed border-[#D9C5B2] p-10 text-center">
              <p className="text-[#6E6860]">No check-ins yet. Your first one is a lovely place to start.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="checkin-list">
              {checkins.map((c) => (
                <div key={c.id} className="rounded-2xl bg-white border border-[#E8E3D9] p-5 flex items-start gap-4" data-testid={`checkin-item-${c.id}`}>
                  <div className="flex-1">
                    <p className="text-xs text-[#A39E94] mb-1">{new Date(c.created_at).toLocaleString()}</p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#2D2A26] bg-[#F5F2EA] rounded-full px-3 py-1">
                        <Smile className="h-3.5 w-3.5 text-[#4A5D4E]" /> {MOOD_LABELS[c.mood]}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#2D2A26] bg-[#F5F2EA] rounded-full px-3 py-1">
                        <Heart className="h-3.5 w-3.5 text-[#C4A265]" /> {CONN_LABELS[c.connection]}
                      </span>
                    </div>
                    {c.note && <p className="text-sm text-[#6E6860] leading-relaxed">{c.note}</p>}
                  </div>
                  <button
                    onClick={() => remove(c.id)}
                    data-testid={`checkin-delete-${c.id}`}
                    className="p-2 rounded-full text-[#A39E94] hover:text-[#B27A70] hover:bg-[#F5F2EA] transition-colors shrink-0"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </motion.div>
    </div>
  );
}

function ScaleRow({ icon: Icon, label, labels, value, onChange, testPrefix }) {
  return (
    <div>
      <label className="flex items-center gap-2 text-base font-medium text-[#2D2A26] mb-3">
        <Icon className="h-4 w-4 text-[#4A5D4E]" strokeWidth={1.75} /> {label}
      </label>
      <div className="flex items-center gap-2 sm:gap-3" role="radiogroup">
        {SCALE.map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`${testPrefix}-rating-${n}`}
            aria-pressed={value === n}
            className={`h-11 w-11 sm:h-12 sm:w-12 rounded-full border text-base font-medium transition-all ${
              value === n
                ? "bg-[#4A5D4E] text-[#FDFBF7] border-[#4A5D4E] scale-105 shadow-sm"
                : "bg-white text-[#2D2A26] border-[#E8E3D9] hover:border-[#4A5D4E]/50 hover:-translate-y-0.5"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      {value ? <p className="mt-2 text-sm text-[#6E6860]">{labels[value]}</p> : null}
    </div>
  );
}
